import { create } from 'zustand';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactNode } from 'react';

interface GenericDrawerState {
  isOpen: boolean;
  title: string;
  component: ReactNode | null;
  onBack?: (() => void) | null;
  actions?: ReactNode | null;
  open: (title: string, component: ReactNode, onBack?: () => void, actions?: ReactNode) => void;
  close: () => void;
}

export const useGenericDrawerStore = create<GenericDrawerState>((set) => ({
  isOpen: false,
  title: '',
  component: null,
  onBack: null,
  actions: null,
  open: (title: string, component: ReactNode, onBack?: () => void, actions?: ReactNode) => set({ isOpen: true, title, component, onBack, actions }),
  close: () => set({ isOpen: false, title: '', component: null, onBack: null }),
}));

export const useGenericDrawer = () => {
  const { open, close, isOpen, title, component, onBack, actions } = useGenericDrawerStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Update URL when drawer state changes
  useEffect(() => {
    if (isOpen) {
      // If drawer is open, add a parameter to indicate it's open
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('drawer', 'open');
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // If drawer is closed, remove the parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('drawer');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [isOpen, searchParams, setSearchParams]);

  const openDrawer = (title: string, component: ReactNode, onBack?: () => void, actions?: ReactNode) => {
    open(title, component, onBack, actions);
  };

  const closeDrawer = () => {
    close();
  };

  return {
    openDrawer,
    closeDrawer,
    onBack,
    onClose: closeDrawer,
    isDrawerOpen: isOpen,
    drawerTitle: title,
    drawerComponent: component,
    drawerActions: actions
  };
}; 